import { useCallback, useRef, useState } from 'react';

import type {
  SearchCoffeesQuery,
  SearchCoffeesQueryVariables,
} from '@/api/generated/graphql';
import { MIN_QUERY_LENGTH, SEARCH_COFFEES_QUERY } from '@/api/queries/search';
import { useAuthedClient } from '@/api/useAuthedClient';
import type { SearchCoffeeResult } from '@/types/search';

import { useGraphQLQuery } from './useGraphQLQuery';

const DEFAULT_LIMIT = 20;

interface UseSearchResultsOptions {
  limit?: number;
  /**
   * Called with the trimmed query and its result count after each successful
   * *first* page for the current query — never for `loadMore` appends, and
   * never for aborted or superseded-query completions (StrictMode-safe).
   */
  onFirstPage?: (query: string, resultCount: number) => void;
}

interface UseSearchResultsReturn {
  results: SearchCoffeeResult[];
  isLoading: boolean;
  error: Error | null;
  loadMore: () => Promise<void>;
  hasMore: boolean;
}

/** A page of results tagged with the query the request was issued for. */
interface SearchPage {
  query: string;
  results: SearchCoffeeResult[];
}

/**
 * Paginated coffee search for the results page. Mirrors `useFeed`'s shape:
 * offset/append tracked in refs, pages accumulated via `onSuccess`.
 */
export function useSearchResults(
  query: string,
  options: UseSearchResultsOptions = {},
): UseSearchResultsReturn {
  const { limit = DEFAULT_LIMIT, onFirstPage } = options;
  const trimmed = query.trim();

  const authedClient = useAuthedClient();

  const [results, setResults] = useState<SearchCoffeeResult[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const offsetRef = useRef(0);
  const appendRef = useRef(false);

  // Reset pagination and clear stale results synchronously when the query
  // changes (render-phase reset, as in useFeed) — the page must not keep
  // showing the previous query's results while the new first page loads.
  const prevQueryRef = useRef(trimmed);
  if (prevQueryRef.current !== trimmed) {
    prevQueryRef.current = trimmed;
    offsetRef.current = 0;
    appendRef.current = false;
    setResults([]);
    setHasMore(true);
  }

  const { isLoading, error, refetch } = useGraphQLQuery<SearchPage>({
    fetcher: async (signal) => {
      const requestQuery = trimmed;
      const response = await authedClient.request<SearchCoffeesQuery, SearchCoffeesQueryVariables>({
        document: SEARCH_COFFEES_QUERY,
        variables: { query: requestQuery, limit, offset: offsetRef.current },
        signal,
      });
      return { query: requestQuery, results: response.searchCoffees };
    },
    deps: [trimmed, limit],
    enabled: trimmed.length >= MIN_QUERY_LENGTH,
    initialLoading: false,
    onSuccess: (page) => {
      // A response can resolve in the window between a query-change render
      // (commit) and the passive-effect cleanup that aborts its request —
      // useGraphQLQuery's abort-signal guard cannot catch that, because the
      // old controller is not yet aborted there. Discard completions whose
      // query has been superseded: this closure is recreated each render
      // (useGraphQLQuery refs it), so `trimmed` is always the latest.
      if (page.query !== trimmed) return;
      const append = appendRef.current;
      appendRef.current = false;
      setResults((prev) => (append ? [...prev, ...page.results] : page.results));
      setHasMore(page.results.length === limit);
      if (!append) {
        onFirstPage?.(trimmed, page.results.length);
      }
    },
  });

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    offsetRef.current += limit;
    appendRef.current = true;
    await refetch();
  }, [isLoading, hasMore, limit, refetch]);

  return { results, isLoading, error, loadMore, hasMore };
}
